/**
 * useFocusAreas
 *
 * React Query hook for listing the current user's focus areas, filtered by
 * status ("active" / "retired" / "suggested" / "all"). Phase 2 envelope:
 * returns the underlying query plus flattened `focusAreas` and
 * `bootstrapJustRan` for convenience.
 */

import { useQuery } from "@tanstack/react-query";
import { listFocusAreas } from "../services/focusAreas";
import type {
  FocusAreaListResponse,
  FocusAreaStatusFilter,
} from "../types/focusArea";

export const FOCUS_AREAS_QUERY_KEY = ["focusAreas"] as const;

export function focusAreasQueryKey(status: FocusAreaStatusFilter) {
  return [...FOCUS_AREAS_QUERY_KEY, status] as const;
}

export function useFocusAreas(status: FocusAreaStatusFilter = "active") {
  const query = useQuery<FocusAreaListResponse, Error>({
    queryKey: focusAreasQueryKey(status),
    queryFn: () => listFocusAreas(status),
    staleTime: 60 * 1000,
  });
  return {
    ...query,
    focusAreas: query.data?.focus_areas ?? [],
    bootstrapJustRan: query.data?.bootstrap_just_ran ?? false,
    activeCount: query.data?.active_count ?? 0,
    maxActive: query.data?.max_active ?? 3,
  };
}
